/**
 * Convert recipe ingredient to purchase quantity
 */
// Helper function to check if keyword matches as a whole word (prevents false positives)
function matchesKeyword(ingredient, keyword) {
    const lowerIngredient = ingredient.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    // Exact match
    if (lowerIngredient === lowerKeyword)
        return true;
    // Word boundary match (keyword at start, end, or surrounded by non-word chars)
    // Escape special regex characters in keyword
    const escapedKeyword = lowerKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const wordBoundaryRegex = new RegExp(`\\b${escapedKeyword}\\b`, 'i');
    return wordBoundaryRegex.test(lowerIngredient);
}
const conversionRules = [
    // Most specific rules first - these will match before more general ones
    // Produce - convert to whole items
    {
        keywords: ['onion', 'shallot'],
        convert: (amount, unit) => ({
            quantity: Math.ceil(amount).toString(),
            unit: 'whole'
        })
    },
    {
        keywords: ['garlic', 'clove'],
        convert: (amount, unit) => {
            // ~3 cloves per head
            const heads = Math.ceil(amount / 3);
            return {
                quantity: heads.toString(),
                unit: heads === 1 ? 'head' : 'heads'
            };
        }
    },
    {
        keywords: ['lemon', 'lime', 'orange'],
        convert: (amount, unit) => ({
            quantity: Math.ceil(amount).toString(),
            unit: unit.includes('zest') ? 'whole (for zest)' : 'whole'
        })
    },
    {
        keywords: ['potato', 'pepper', 'bell pepper', 'avocado'],
        convert: (amount, unit) => ({
            quantity: Math.ceil(amount).toString(),
            unit: 'whole'
        })
    },
    {
        keywords: ['tomato'],
        convert: (amount, unit) => ({
            quantity: Math.ceil(amount).toString(),
            unit: 'whole'
        })
    },
    {
        keywords: ['crushed tomatoes', 'canned tomatoes', 'diced tomatoes'],
        convert: (amount, unit) => {
            let cans = amount;
            if (unit === 'oz' || unit === 'ounce') {
                // Assume 14-15 oz cans
                cans = Math.ceil(amount / 14);
            }
            else if (unit === 'cup' || unit === 'cups') {
                // 1 cup ≈ 8 oz, so ~2 cups per can
                cans = Math.ceil(amount / 2);
            }
            // If already in 'can' units, use as-is
            return {
                quantity: cans.toString(),
                unit: cans === 1 ? 'can (14 oz)' : 'cans (14 oz)'
            };
        }
    },
    {
        keywords: ['ginger', 'fresh ginger'],
        convert: (amount, unit) => ({
            quantity: Math.ceil(amount).toString(),
            unit: unit === 'inch' || unit === 'inches' ? 'piece' : 'whole'
        })
    },
    // Flour and grains - convert to bags/packages
    {
        keywords: ['flour', 'all-purpose flour', 'bread flour'],
        convert: (amount, unit) => {
            // Convert to cups first
            let cups = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                cups = amount / 16;
            }
            else if (unit === 'tsp' || unit === 'teaspoon') {
                cups = amount / 48;
            }
            else if (unit === 'lb' || unit === 'pound' || unit === 'lbs') {
                cups = amount * 3.5; // ~3.5 cups per lb
            }
            // 5lb bag = ~17.5 cups
            const bags = Math.ceil(cups / 17.5);
            return {
                quantity: bags.toString(),
                unit: bags === 1 ? '5 lb bag' : '5 lb bags'
            };
        }
    },
    {
        keywords: ['sugar', 'brown sugar', 'granulated sugar'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                cups = amount / 16;
            }
            else if (unit === 'lb' || unit === 'pound') {
                cups = amount * 2; // ~2 cups per lb
            }
            // 4lb bag = ~8 cups
            const bags = Math.ceil(cups / 8);
            return {
                quantity: bags.toString(),
                unit: bags === 1 ? '4 lb bag' : '4 lb bags'
            };
        }
    },
    {
        keywords: ['rice', 'white rice', 'brown rice'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'lb' || unit === 'pound') {
                cups = amount * 2.5; // ~2.5 cups per lb
            }
            // 1lb bag = ~2.5 cups
            const bags = Math.ceil(cups / 2.5);
            return {
                quantity: bags.toString(),
                unit: bags === 1 ? '1 lb bag' : '1 lb bags'
            };
        }
    },
    // Dairy - convert to packages
    {
        keywords: ['butter'],
        convert: (amount, unit) => {
            let sticks = amount;
            if (unit === 'cup' || unit === 'cups') {
                sticks = amount * 2; // 1 cup = 2 sticks
            }
            else if (unit === 'tbsp' || unit === 'tablespoon') {
                sticks = amount / 8; // 1 stick = 8 tbsp
            }
            const packages = Math.ceil(sticks / 4); // 4 sticks per package
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? 'package (4 sticks)' : 'packages'
            };
        }
    },
    {
        keywords: ['milk', 'whole milk', '2% milk'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'fl oz' || unit === 'fluid ounce') {
                cups = amount / 8;
            }
            // 1 gallon = 16 cups
            const gallons = Math.ceil(cups / 16);
            if (gallons <= 1 && cups <= 4) {
                return {
                    quantity: '1',
                    unit: 'half gallon'
                };
            }
            return {
                quantity: gallons.toString(),
                unit: gallons === 1 ? 'gallon' : 'gallons'
            };
        }
    },
    {
        keywords: ['cheese', 'cheddar', 'mozzarella', 'parmesan'],
        convert: (amount, unit) => {
            let oz = amount;
            if (unit === 'cup' || unit === 'cups') {
                oz = amount * 4; // ~4 oz per cup shredded
            }
            else if (unit === 'lb' || unit === 'pound') {
                oz = amount * 16;
            }
            // 8 oz package
            const packages = Math.ceil(oz / 8);
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? '8 oz package' : '8 oz packages'
            };
        }
    },
    {
        keywords: ['yogurt', 'plain yogurt', 'greek yogurt'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                cups = amount / 16;
            }
            else if (unit === 'fl oz' || unit === 'fluid ounce') {
                cups = amount / 8;
            }
            // 32 oz container = 4 cups
            const containers = Math.ceil(cups / 4);
            return {
                quantity: containers.toString(),
                unit: containers === 1 ? '32 oz container' : '32 oz containers'
            };
        }
    },
    {
        keywords: ['heavy cream', 'whipping cream', 'cream'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                cups = amount / 16;
            }
            else if (unit === 'fl oz' || unit === 'fluid ounce') {
                cups = amount / 8;
            }
            // 1 pint = 2 cups, but often sold in smaller containers
            if (cups <= 1) {
                return {
                    quantity: '1',
                    unit: '8 fl oz container'
                };
            }
            const pints = Math.ceil(cups / 2);
            return {
                quantity: pints.toString(),
                unit: pints === 1 ? 'pint' : 'pints'
            };
        }
    },
    {
        keywords: ['eggs', 'egg'],
        convert: (amount, unit) => {
            const dozens = Math.ceil(amount / 12);
            return {
                quantity: dozens.toString(),
                unit: dozens === 1 ? 'dozen' : 'dozens'
            };
        }
    },
    // Meat and seafood - convert to packages
    {
        keywords: ['boneless skinless chicken', 'chicken thighs', 'chicken breast'],
        convert: (amount, unit) => {
            let lbs = amount;
            if (unit === 'oz' || unit === 'ounce') {
                lbs = amount / 16;
            }
            // Typically sold in 1-2 lb packages
            const packages = Math.ceil(lbs / 1.5);
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? 'package (~1.5 lbs)' : 'packages'
            };
        }
    },
    {
        keywords: ['chicken'],
        convert: (amount, unit) => {
            let lbs = amount;
            if (unit === 'oz' || unit === 'ounce') {
                lbs = amount / 16;
            }
            // If already in lbs, keep as lbs; otherwise convert to package
            if (unit === 'lb' || unit === 'lbs' || unit === 'pound') {
                return {
                    quantity: Math.ceil(lbs).toString(),
                    unit: 'lb'
                };
            }
            const packages = Math.ceil(lbs / 1.5);
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? 'package (~1.5 lbs)' : 'packages'
            };
        }
    },
    {
        keywords: ['beef', 'ground beef', 'steak'],
        convert: (amount, unit) => {
            let lbs = amount;
            if (unit === 'oz' || unit === 'ounce') {
                lbs = amount / 16;
            }
            const packages = Math.ceil(lbs / 1);
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? '1 lb package' : '1 lb packages'
            };
        }
    },
    {
        keywords: ['salmon', 'fish', 'tuna', 'tilapia'],
        convert: (amount, unit) => {
            let lbs = amount;
            if (unit === 'oz' || unit === 'ounce') {
                lbs = amount / 16;
            }
            const packages = Math.ceil(lbs / 0.75); // Typically 0.75-1 lb fillets
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? 'fillet (~0.75 lb)' : 'fillets'
            };
        }
    },
    // Spices and seasonings - convert to containers
    {
        keywords: ['salt', 'table salt', 'kosher salt'],
        convert: (amount, unit) => {
            let tsp = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                tsp = amount * 3;
            }
            else if (unit === 'cup' || unit === 'cups') {
                tsp = amount * 48;
            }
            // Small container = ~26 oz = ~1500 tsp
            if (tsp > 100) {
                return {
                    quantity: '1',
                    unit: 'large container (26 oz)'
                };
            }
            return {
                quantity: '1',
                unit: 'small container (16 oz)'
            };
        }
    },
    {
        keywords: ['pepper', 'black pepper'],
        convert: (amount, unit) => {
            return {
                quantity: '1',
                unit: 'container (2-4 oz)'
            };
        }
    },
    {
        keywords: ['paprika', 'cumin', 'oregano', 'thyme', 'rosemary', 'basil'],
        convert: (amount, unit) => {
            return {
                quantity: '1',
                unit: 'container (1-2 oz)'
            };
        }
    },
    // Oils and liquids
    {
        keywords: ['olive oil', 'vegetable oil', 'canola oil'],
        convert: (amount, unit) => {
            let cups = amount;
            if (unit === 'tbsp' || unit === 'tablespoon') {
                cups = amount / 16;
            }
            else if (unit === 'fl oz' || unit === 'fluid ounce') {
                cups = amount / 8;
            }
            // 16.9 fl oz bottle = ~2 cups
            if (cups <= 2) {
                return {
                    quantity: '1',
                    unit: 'bottle (16.9 fl oz)'
                };
            }
            const bottles = Math.ceil(cups / 2);
            return {
                quantity: bottles.toString(),
                unit: bottles === 1 ? 'bottle (16.9 fl oz)' : 'bottles'
            };
        }
    },
    // Pasta and noodles
    {
        keywords: ['pasta', 'spaghetti', 'penne', 'fettuccine', 'noodles'],
        convert: (amount, unit) => {
            let oz = amount;
            if (unit === 'lb' || unit === 'pound') {
                oz = amount * 16;
            }
            // Typically sold in 16 oz boxes
            const boxes = Math.ceil(oz / 16);
            return {
                quantity: boxes.toString(),
                unit: boxes === 1 ? '16 oz box' : '16 oz boxes'
            };
        }
    },
    // Canned goods
    {
        keywords: ['canned tomatoes', 'tomato sauce', 'tomato paste'],
        convert: (amount, unit) => {
            let cans = amount;
            if (unit === 'oz' || unit === 'ounce') {
                // Assume 14-15 oz cans
                cans = Math.ceil(amount / 14);
            }
            else if (unit === 'cup' || unit === 'cups') {
                // 1 cup ≈ 8 oz, so ~2 cups per can
                cans = Math.ceil(amount / 2);
            }
            return {
                quantity: cans.toString(),
                unit: cans === 1 ? '14 oz can' : '14 oz cans'
            };
        }
    },
    // Bread and bakery
    {
        keywords: ['bread', 'loaf'],
        convert: (amount, unit) => {
            return {
                quantity: '1',
                unit: 'loaf'
            };
        }
    },
    {
        keywords: ['tortillas'],
        convert: (amount, unit) => {
            const packages = Math.ceil(amount / 10); // Typically 10 per package
            return {
                quantity: packages.toString(),
                unit: packages === 1 ? 'package (10 count)' : 'packages'
            };
        }
    }
];
/**
 * Convert recipe ingredient to purchase quantity
 */
export function convertToPurchaseQuantity(amount, unit, ingredient) {
    // Handle undefined/null ingredient
    if (!ingredient) {
        return {
            quantity: Math.ceil(amount || 1).toString(),
            unit: unit || "units",
            item: "Unknown ingredient"
        };
    }
    const lowerIngredient = ingredient.toLowerCase().trim();
    // Find matching conversion rule (most specific first)
    for (const rule of conversionRules) {
        for (const keyword of rule.keywords) {
            if (matchesKeyword(lowerIngredient, keyword)) {
                const converted = rule.convert(amount, unit.toLowerCase());
                return Object.assign(Object.assign({}, converted), { item: ingredient });
            }
        }
    }
    // Default: smart fallback based on unit
    if (!unit || unit === '') {
        // No unit specified - assume whole items
        return {
            quantity: Math.ceil(amount || 1).toString(),
            unit: 'whole',
            item: ingredient
        };
    }
    // Has unit - keep original but round up
    return {
        quantity: Math.ceil(amount || 1).toString(),
        unit: unit,
        item: ingredient
    };
}
/**
 * Parse ingredient string and convert
 * Handles formats like "2 cups flour" or "1/2 onion"
 */
export function parseAndConvert(ingredientText) {
    // Simple parsing - extract number, unit, and ingredient
    const fractionMap = {
        "1/2": 0.5,
        "1/3": 0.33,
        "1/4": 0.25,
        "2/3": 0.67,
        "3/4": 0.75
    };
    let amount = 1;
    let unit = "";
    let ingredient = ingredientText;
    // Try to extract amount
    const numberMatch = ingredientText.match(/^([\d.]+|\d+\/\d+)\s*/);
    if (numberMatch) {
        const numStr = numberMatch[1];
        amount = fractionMap[numStr] || parseFloat(numStr) || 1;
        ingredient = ingredientText.replace(numberMatch[0], "").trim();
    }
    // Extract unit (first word after number)
    const words = ingredient.split(/\s+/);
    if (words.length > 1) {
        unit = words[0];
        ingredient = words.slice(1).join(" ");
    }
    return convertToPurchaseQuantity(amount, unit, ingredient);
}
